import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { push } from "./push";
import { quienSoy } from "../../../appMovil/contadores";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { AnunciosFuente, EntradaEquipo, FiltroGuardado, MiGestionConteo, ProspectCatalogs, ProspectFilters, ProspectRow } from "../../../services/v2/prospects.service";
import { CURSOS } from "./cursosSelect";
import { cuando, cuandoViene, errorTexto, ETAPAS, fechaLarga, limpiarWa, nombreReal, nombreWa, pareceNombre } from "./salesHelpers";
import { interesCorto, interesesCortos } from "../../../utils/cursoCorto";
import { MoverModal } from "./moverModal";
import { AREA, primerNombre } from "./equipoChips";

/** "Cejas, Pestañas y Microblading; Manicure y Acrilismo" → "Cejas, Manicure" (los nombres cortos de siempre). */
export const cursosCortos = (t: string | null | undefined): string => interesesCortos(t).join(", ");

const PER_PAGE = 100;

/** Filtros fijos del CRM (prototipo aprobado). Las llaves son las vistas de ProspectService (VIEWS_CRM). */
const FILTROS: { key: string; label: string; oro?: boolean }[] = [
	{ key: "creaciones_hoy", label: "Creaciones de hoy" },
	// Esteban, 2026-10-12: los Nuevo Cliente sin nada agendado son los que se pierden (oro: hay que atenderlos).
	{ key: "nuevos_sin_act", label: "Clientes nuevos sin actividad", oro: true },
	{ key: "nuevos_con_act", label: "Clientes nuevos con actividad" },
	{ key: "act_hoy", label: "Actividades de hoy" },
	{ key: "manana", label: "Actividades de mañana" },
	{ key: "semana", label: "Actividades de esta semana" },
	{ key: "act_proximas", label: "Actividades próximas" },
	{ key: "vencidas", label: "Actividades vencidas", oro: true },
	{ key: "notas_hoy", label: "Notas de hoy" },
	{ key: "notas_7d", label: "Notas de los últimos 7 días" },
	{ key: "interesados", label: "Interesados" },
	{ key: "promesas", label: "Promesas" },
	{ key: "sin_nota_30", label: "Sin nota en 30 días" },
];

export const plantelCorto = (catalogs: ProspectCatalogs | null, id: number | null | undefined): string =>
	(catalogs?.campuses.find((c) => c.id === id)?.name || "").replace("Washington Academy ", "") || "—";

/** Las tareas de Zoho solo traen el día y vencen a las 23:59 de México: se muestran sin hora. */
export const sinHoraSiTodoElDia = (at: string | null | undefined): string | null => {
	if (!at) return null;
	const t = new Date(at.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(at) ? "" : "Z")).getTime() - 6 * 3600000;
	if (isNaN(t)) return at;
	const iso = new Date(t).toISOString();
	return iso.slice(11, 16) === "23:59" ? iso.slice(0, 10) : at;
};

/** "Mañana 10:00 am · Llamar: confirmar horario" / "Vencida 28 sep · Llamar". */
export const proximaTexto = (at: string | null, razon: string | null): { texto: string; vencida: boolean } | null => {
	const v = cuandoViene(sinHoraSiTodoElDia(at));
	if (!v) return null;
	return { texto: v.texto.replace(/^Vencido/, "Vencida") + (razon ? " · " + razon : ""), vencida: v.tono === "vencido" };
};

/** El logo de WhatsApp (su globo con el teléfono), chico, para marcar que ese es su nombre en WhatsApp. */
export const LogoWa: React.FC<{ size?: number; label?: string }> = ({ size = 14, label = "Nombre en WhatsApp" }) => (
	<svg className="logo-wa" width={size} height={size} viewBox="0 0 24 24" aria-label={label} role="img">
		<path fill="#25D366" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2z" />
		<path fill="#fff" d="M17.2 14.3c-.3-.1-1.7-.8-1.9-.9-.3-.1-.5-.1-.7.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.2-1.4-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.1c-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.5.1-.7.3-.3.3-1 .9-1 2.3s1 2.7 1.2 2.9c.1.2 2 3.1 4.9 4.3 2.4 1 2.9.8 3.4.7.5 0 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z" />
	</svg>
);

// El nombre de WhatsApp y su limpieza viven en salesHelpers (un solo cálculo del nombre en CRM, ficha y WappChat).
export { limpiarWa, nombreWa, pareceNombre, waDiceAlgo } from "./salesHelpers";

/** Primer nombre y primer apellido, como en Matrículas (Esteban, 2026-10-06: "el nombre está demasiado extensivo"). */
export const nombreLista = (p: { first_name?: string | null; last_name?: string | null }): string | null => {
	const real = nombreReal(p);
	if (!real) return null;
	const n = (p.first_name || "").trim().split(/\s+/).filter(Boolean), a = (p.last_name || "").trim().split(/\s+/).filter(Boolean);
	if (n.length && a.length) return n[0] + " " + a[0];
	const w = real.split(/\s+/);
	return w.length <= 2 ? real : w[0] + " " + w[w.length >= 4 ? 2 : w.length - 1];
};

/** Banderín de la actividad como en Zoho: "7 oct", "Hoy 4 pm", "Vencida 3 oct". */
export const banderin = (at: string | null | undefined): { t: string; tono: string } | null => {
	const v = cuandoViene(sinHoraSiTodoElDia(at));
	if (!v) return null;
	// Vencida: solo la fecha (el rojo ya dice que venció; así cabe en una laptop de 13").
	if (v.tono === "vencido") return { t: v.texto.replace(/^Vencid[oa]\s*·?\s*/i, "") || v.texto, tono: "vencida" };
	if (/^hoy/i.test(v.texto)) return { t: v.texto, tono: "hoy" };
	return { t: v.texto, tono: "luego" };
};

/** Filtros por campo, como "Filtrar clientes por" de Zoho (Esteban, 2026-10-06). */
interface Campos {
	status: number[]; etapas: number[]; planteles: number[]; duenas: string[]; equipos: string[]; cursos: string[]; source: number[];
	creado_desde: string; creado_hasta: string; contacto_desde: string; contacto_hasta: string; sin_contacto_dias: string; actividad: string;
	/** Fuente (2026-10-09): anuncio o campaña de Meta por el que llegó alguna vez, y número de WhatsApp al que escribió. */
	anuncios: string[]; campanas: string[]; canales: number[];
}
const VACIO: Campos = { status: [], etapas: [], planteles: [], duenas: [], equipos: [], cursos: [], source: [], creado_desde: "", creado_hasta: "",
	contacto_desde: "", contacto_hasta: "", sin_contacto_dias: "", actividad: "", anuncios: [], campanas: [], canales: [] };
const cuantos = (c: Campos) => (Object.keys(c) as (keyof Campos)[]).filter((k) => (Array.isArray(c[k]) ? (c[k] as any[]).length : !!c[k])).length;

/** Día local (México/Honduras) en "AAAA-MM-DD", corrido n días. */
const diaLocal = (n = 0) => new Date(Date.now() - 6 * 3600000 + n * 86400000).toISOString().slice(0, 10);
const RANGOS: { k: string; t: string; d: () => [string, string] }[] = [
	{ k: "hoy", t: "Hoy", d: () => [diaLocal(), diaLocal()] },
	{ k: "ayer", t: "Ayer", d: () => [diaLocal(-1), diaLocal(-1)] },
	{ k: "sem", t: "Esta semana", d: () => { const dow = (new Date(diaLocal() + "T12:00:00Z").getUTCDay() + 6) % 7; return [diaLocal(-dow), diaLocal()]; } },
	{ k: "7", t: "Últimos 7 días", d: () => [diaLocal(-6), diaLocal()] },
	{ k: "30", t: "Últimos 30 días", d: () => [diaLocal(-29), diaLocal()] },
	{ k: "mes", t: "Este mes", d: () => [diaLocal().slice(0, 8) + "01", diaLocal()] },
	{ k: "mesp", t: "Mes pasado", d: () => {
		const h = new Date(diaLocal().slice(0, 8) + "01T12:00:00Z"); const fin = new Date(h.getTime() - 86400000);
		return [fin.toISOString().slice(0, 8) + "01", fin.toISOString().slice(0, 10)];
	} },
];
/** "Entradas de…" en el riel (Esteban, 2026-10-15): el periodo usa los mismos rangos que el filtro "Creado / recontacto". */
const PERIODOS_ENT: { k: string; t: string; titulo: string; rango: string }[] = [
	{ k: "hoy", t: "Hoy", titulo: "Hoy", rango: "hoy" }, { k: "ayer", t: "Ayer", titulo: "Ayer", rango: "ayer" },
	{ k: "semana", t: "Esta Semana", titulo: "Esta Semana", rango: "sem" }, { k: "7", t: "Últimos 7 Días", titulo: "los Últimos 7 Días", rango: "7" },
	{ k: "mes", t: "Este Mes", titulo: "Este Mes", rango: "mes" }, { k: "30", t: "Últimos 30 Días", titulo: "los Últimos 30 Días", rango: "30" },
];
const periodoDe = (k: string) => PERIODOS_ENT.find((p) => p.k === k) || PERIODOS_ENT[0];
const rangoEnt = (k: string): [string, string] => (RANGOS.find((x) => x.k === periodoDe(k).rango) || RANGOS[0]).d();
/** "12 Creaciones · 7 Recontactos" completo en vez de "12 C · 7 R" (Esteban, 2026-10-15); lo que va en 0 no se dice y, si no
 * cabe, se parte entre las dos mitades (nunca a media palabra). */
const CyR: React.FC<{ c: number; r: number }> = ({ c, r }) => {
	const cc = c + (c === 1 ? " Creación" : " Creaciones"), rr = r + (r === 1 ? " Recontacto" : " Recontactos");
	return <span className="cl-cyr">{c || !r ? <span>{cc}</span> : null}{c && r ? " · " : null}{r ? <span>{rr}</span> : null}</span>;
};
const ACTIVIDAD: { k: string; t: string }[] = [
	{ k: "", t: "Cualquiera" }, { k: "vencida", t: "Vencida" }, { k: "hoy", t: "Hoy" }, { k: "semana", t: "Próximos 7 días" },
	{ k: "con", t: "Con actividad" }, { k: "ninguna", t: "Sin actividad" },
];

/** Columnas de la tabla: se eligen en "Columnas" (se recuerda en este navegador) y se ordenan con un clic. */
type Col = { k: string; t: string; sort?: string; sup?: boolean; def: boolean };
/** Peso de cada columna (≈ px a 1280): la tabla reparte el ancho disponible (sin scroll de lado en una laptop de 13"). */
const PESO: { [k: string]: number } = { proxima: 82, nombre: 142, status: 88, etapa: 114, curso: 92, movil: 140, territorio: 76, plantel: 80, duena: 132, equipo: 84, fuente: 80, anuncio: 120, creado: 74, contacto: 82 };
/** En pantalla angosta (< 1200 px) el móvil va sin lada: le sobra ancho y se lo damos a Nombre, Curso y Territorio. */
const PESO_ANGOSTA: { [k: string]: number } = { movil: 126, nombre: 158, curso: 100, territorio: 78 };
const MQ_ANGOSTA = "(max-width: 1199px)";
const useAngosta = (): boolean => useMedia(MQ_ANGOSTA);
/** En el teléfono la tarjeta lleva siempre lo mismo, aunque en la PC haya elegido otras columnas. */
const COLS_TEL = ["nombre", "creado", "status", "etapa", "curso", "proxima", "movil", "duena"];
const useMedia = (q: string): boolean => {
	const [a, setA] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(q).matches);
	useEffect(() => {
		if (!window.matchMedia) return;
		const mq = window.matchMedia(q), f = () => setA(mq.matches);
		mq.addEventListener ? mq.addEventListener("change", f) : mq.addListener(f);
		return () => { mq.removeEventListener ? mq.removeEventListener("change", f) : mq.removeListener(f); };
	}, [q]);
	return a;
};
const COLUMNAS: Col[] = [
	{ k: "proxima", t: "Actividad", sort: "proxima", def: true },
	{ k: "nombre", t: "Nombre", sort: "nombre", def: true },
	{ k: "status", t: "Status", sort: "status", def: true },
	{ k: "etapa", t: "Etapa", sort: "etapa", def: true },
	{ k: "curso", t: "Curso", sort: "curso", def: true },
	{ k: "movil", t: "Móvil", sort: "movil", def: true },
	{ k: "territorio", t: "Territorio", def: true },
	{ k: "plantel", t: "Plantel", sort: "plantel", def: false },
	{ k: "duena", t: "Dueñas", sort: "duena", sup: true, def: true },
	{ k: "equipo", t: "Equipo", sort: "equipo", sup: true, def: true },
	{ k: "fuente", t: "Llegó por", def: false },
	{ k: "anuncio", t: "Anuncio", def: false },
	{ k: "creado", t: "Creado / recontacto", sort: "entrada", def: true },
	{ k: "contacto", t: "Últ. contacto", sort: "contacto", def: true },
];
const LLAVE_COLS = "wapp.crm.columnas.v7";
const leerCols = (): string[] => {
	try {
		const v = JSON.parse(localStorage.getItem(LLAVE_COLS) || "null"); if (Array.isArray(v) && v.length) return v;
		// v6 -> v7 (2026-10-06): Móvil y Territorio pasan a verse siempre; se agregan después de Curso sin perder el orden de la asesora.
		const v6 = JSON.parse(localStorage.getItem("wapp.crm.columnas.v6") || "null");
		if (Array.isArray(v6) && v6.length) {
			const nuevas = ["movil", "territorio"].filter((k) => !v6.includes(k)), i = v6.indexOf("curso");
			return i >= 0 ? [...v6.slice(0, i + 1), ...nuevas, ...v6.slice(i + 1)] : [...v6, ...nuevas];
		}
	} catch { /* sin almacenamiento */ }
	return COLUMNAS.filter((c) => c.def).map((c) => c.k);
};

/** Territorio de Ventas (los del router): por plantel; Online o sin plantel, por la lada del teléfono. */
export const territorio = (r: { campus_id?: number | null; phone_e164?: string | null }): string => {
	const c = Number(r.campus_id);
	if (c === 3 || c === 4) return "Honduras";
	if (c === 1) return "Monterrey";
	if (c === 2) return "Torreón";
	const t = (r.phone_e164 || "").replace(/^\+/, "");
	return t.startsWith("504") ? "Honduras" : t.startsWith("52") ? "Monterrey" : "";
};

/** Popover que se cierra al tocar fuera o con Escape. */
const usePop = (abierto: boolean, cerrar: () => void) => {
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!abierto) return;
		const f = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) cerrar(); };
		const k = (e: KeyboardEvent) => { if (e.key === "Escape") cerrar(); };
		document.addEventListener("mousedown", f); document.addEventListener("keydown", k);
		return () => { document.removeEventListener("mousedown", f); document.removeEventListener("keydown", k); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [abierto]);
	return ref;
};

/** Colores de status (los mismos de la ficha). Nuevo Cliente y Recontactó van llenos: son los que hay que atender ya. */
/*
	Lógica de color (Esteban, 2026-10-06):
	- VERDE SOLO PARA MATRICULADO: antes de la venta nada es verde ("hace sonar como que algo ya se vendió").
	- Status = píldora RELLENA, de más caliente a más frío: Nuevo Cliente y Recontactó naranja lleno (los dos hay que atenderlos ya),
	  Promesa azul lleno, Interesado azul claro, Potencial rosa; lo que saca de seguimiento en oro (lo malo es oro).
	- Etapa = CONTORNO SÓLIDO con la letra del mismo color: Pre-Inscripción gris, Matriculado verde, Graduado negro,
	  Retirado café. Ningún color se repite entre status y etapa.
	- Actividad = banderín como en Zoho: hoy naranja de marca, vencida roja, más adelante azul marino del logo.
*/
export const ST_PILL: { [code: string]: { bg: string; fg: string; lleno?: boolean } } = {
	nuevo_cliente: { bg: "#F45030", fg: "#fff", lleno: true }, recontacto: { bg: "#F45030", fg: "#fff", lleno: true },
	promesa: { bg: "#1D4BB8", fg: "#fff", lleno: true }, interesado: { bg: "#DCE6FB", fg: "#1D4BB8" }, potencial: { bg: "#E6E8EC", fg: "#4B5563" },
	matriculado: { bg: "#1F8A5B", fg: "#fff", lleno: true },   // el único status verde: ya se vendió
	numero_equivocado: { bg: "#F3E6C8", fg: "#6E4C06" }, no_contactar: { bg: "#F3E6C8", fg: "#6E4C06" }, spam: { bg: "#F3E6C8", fg: "#6E4C06" },
};
export const ETAPA_BORDE: { [id: number]: string } = { 1: "#0A78B5", 2: "#1F8A5B", 3: "#7B4B2A", 4: "#2B1C16" };

/** Filtro rápido de la barra: botón con lo elegido y lista con casillas (y buscador si es larga). */
const MultiFiltro: React.FC<{ titulo: string; ops: { v: any; t: string }[]; sel: any[]; onChange: (v: any[]) => void; buscar?: boolean }> = ({ titulo, ops, sel, onChange, buscar }) => {
	const [abierto, setAbierto] = useState(false);
	const ref = usePop(abierto, () => setAbierto(false));
	const texto = !sel.length ? titulo : sel.length === 1 ? (ops.find((o) => o.v === sel[0])?.t || titulo) : titulo + " · " + sel.length;
	return (
		<div className="cl-mf" ref={ref}>
			<button type="button" className={"cl-mf-btn" + (sel.length ? " activo" : "")} aria-expanded={abierto} onClick={() => setAbierto(!abierto)}>
				<span className="cl-mf-t">{texto}</span>
				<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
			</button>
			{abierto ? (
				<div className="cl-pop cl-pop-izq" role="dialog" aria-label={titulo}>
					<div className="cl-pop-cab"><b>{titulo}</b>{sel.length ? <button className="cl-link" onClick={() => onChange([])}>Quitar</button> : null}</div>
					<Casillas ops={ops} sel={sel} onChange={onChange} buscar={buscar || ops.length > 10} />
				</div>
			) : null}
		</div>
	);
};

/** Periodo de "Entradas de…": Hoy, Ayer, Esta Semana (lunes a hoy), Últimos 7 Días, Este Mes (del 1 a hoy), Últimos 30 Días.
 *  El select va invisible encima del texto: el iPhone le pinta su propia flecha aunque lleve appearance: none. */
const SelPeriodo: React.FC<{ valor: string; onChange: (k: string) => void }> = ({ valor, onChange }) => (
	<span className="cl-ent-sel">
		<span className="cl-ent-sel-t" aria-hidden="true">{periodoDe(valor).t}</span>
		<select value={valor} onChange={(e) => onChange(e.target.value)} aria-label="Periodo de las entradas">
			{PERIODOS_ENT.map((p) => <option key={p.k} value={p.k}>{p.t}</option>)}
		</select>
		<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
	</span>
);

/** Fecha de creación con botones (hoy, esta semana…) y rango a mano. */
const FiltroFecha: React.FC<{ titulo: string; desde: string; hasta: string; onChange: (d: string, h: string) => void }> = ({ titulo, desde, hasta, onChange }) => {
	const [abierto, setAbierto] = useState(false);
	const ref = usePop(abierto, () => setAbierto(false));
	const preset = RANGOS.find((x) => { const [a, b] = x.d(); return a === desde && b === hasta; });
	const texto = preset ? titulo + ": " + preset.t : desde || hasta ? titulo + ": " + (desde || "…") + " a " + (hasta || "…") : titulo;
	return (
		<div className="cl-mf" ref={ref}>
			<button type="button" className={"cl-mf-btn" + (desde || hasta ? " activo" : "")} aria-expanded={abierto} onClick={() => setAbierto(!abierto)}>
				<span className="cl-mf-t">{texto}</span>
				<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
			</button>
			{abierto ? (
				<div className="cl-pop cl-pop-izq cl-pop-fecha" role="dialog" aria-label={titulo}>
					<div className="cl-pop-cab"><b>{titulo}</b>{desde || hasta ? <button className="cl-link" onClick={() => onChange("", "")}>Quitar</button> : null}</div>
					<div className="cl-rangos">
						{RANGOS.map((x) => {
							const [a, b] = x.d();
							const on = a === desde && b === hasta;
							return <button key={x.k} type="button" className={"cl-rango" + (on ? " on" : "")} onClick={() => { on ? onChange("", "") : onChange(a, b); setAbierto(false); }}>{x.t}</button>;
						})}
					</div>
					<div className="cl-fechas">
						<label>Desde<input type="date" value={desde} onChange={(e) => onChange(e.target.value, hasta)} /></label>
						<label>Hasta<input type="date" value={hasta} onChange={(e) => onChange(desde, e.target.value)} /></label>
					</div>
				</div>
			) : null}
		</div>
	);
};

const Casillas: React.FC<{ ops: { v: any; t: string; n?: number }[]; sel: any[]; onChange: (v: any[]) => void; buscar?: boolean; una?: boolean }> = ({ ops, sel, onChange, buscar, una }) => {
	const [b, setB] = useState("");
	const vis = buscar && b.trim() ? ops.filter((o) => o.t.toLowerCase().includes(b.trim().toLowerCase())) : ops;
	return (
		<div className="cl-casillas">
			{buscar ? <input className="cl-in" placeholder="Buscar" value={b} onChange={(e) => setB(e.target.value)} aria-label="Buscar" /> : null}
			<div className={"cl-casillas-lista" + (una ? " una" : "")}>
				{vis.map((o) => {
					const on = sel.includes(o.v);
					return (
						<label key={String(o.v)} className={"cl-casilla" + (on ? " on" : "")}>
							<input type="checkbox" checked={on} onChange={() => onChange(on ? sel.filter((x) => x !== o.v) : [...sel, o.v])} />{una ? <span className="cl-casilla-t" title={o.t}>{o.t}</span> : o.t}
							{o.n !== undefined ? <span className="cl-casilla-n">{o.n.toLocaleString("es-MX")}</span> : null}
						</label>
					);
				})}
			</div>
		</div>
	);
};

/** "Sofía Elizabeth Bardales" → "Sofía Bardales" (nombre y primer apellido, como en Matrículas). */
const corto = (t: string) => { const w = t.replace(/\s+(TRC|MTY|SPS|TGU)$/i, "").split(/\s+/); return w.length <= 2 ? w.join(" ") : w[0] + " " + w[w.length >= 4 ? 2 : w.length - 1]; };
/** Varias dueñas en una celda: nombre e inicial del apellido ("Deisy R."), para que quepa el área al lado. */
const inicial = (t: string) => { const w = corto(t).split(" "); return w.length > 1 ? w[0] + " " + w[1].charAt(0) + "." : w[0]; };

/** Sin orden elegido la lista sale por último contacto, lo más reciente arriba (lo que hace el servidor). */
const ORDEN_DEF = { sort: "entrada", dir: "desc" };   // creación o recontacto, lo más nuevo arriba (Esteban, 2026-10-06)
const DIR_TXT: { [k: string]: [string, string] } = {
	nombre: ["A → Z", "Z → A"], movil: ["menor a mayor", "mayor a menor"], etapa: ["Pre-Inscripción primero", "Graduado primero"],
	status: ["Nuevo primero", "Spam primero"], curso: ["A → Z", "Z → A"], plantel: ["A → Z", "Z → A"], duena: ["A → Z", "Z → A"], equipo: ["A → Z", "Z → A"],
	creado: ["los más viejos primero", "los más nuevos primero"], entrada: ["los más viejos primero", "los más nuevos primero"], contacto: ["el más viejo primero", "el más reciente primero"], proxima: ["la más próxima primero", "la más lejana primero"],
};

interface Props {
	campusId: number;
	catalogs: ProspectCatalogs | null;
	onOpen: (id: number, titulo: string) => void;
	onNuevo: () => void;
	/** Abre su conversación en WappChat (solo quien ve WappChat). */
	onChat?: (id: number) => void;
	/** Sube cuando la ficha cambió algo: la lista vuelve a pedir la página. */
	refresco: number;
	/** Para las flechas anterior/siguiente de la ficha: los de esta página y cómo pedir otra. */
	onNav?: (nav: ListaNav) => void;
}

export interface ListaNav { ids: number[]; page: number; total: number; perPage: number; ir: (p: number) => Promise<number[]> }

/**
 * La lista del CRM, como la de Zoho: filtros guardados a la izquierda (los fijos, los míos y los compartidos),
 * filtros por cualquier campo, columnas a elegir y orden con un clic en el encabezado.
 * La asesora ve su cartera de todos sus planteles; Supervisión elige de quién.
 */
const CrmLista: React.FC<Props> = ({ campusId, catalogs, onOpen, onNuevo, onChat, refresco, onNav }) => {
	const http = HttpManager.getInstance();
	const sup = !!catalogs?.isSupervisor;
	// Supervisión arranca viendo a todos (como en WappChat); se espera al catálogo para no pedir dos veces.
	const [owner, setOwner] = useState<string | null>(null);
	const [view, setView] = useState<string | null>(null);
	// "Mis gestiones" (Esteban, 2026-10-12): las gestiones que llevo yo, por objetivo y estado ("kit:pendiente").
	const [gestion, setGestion] = useState<string | null>(null);
	const [misG, setMisG] = useState<MiGestionConteo[]>([]);
	const [campos, setCampos] = useState<Campos>(VACIO);
	const [orden, setOrden] = useState<{ sort: string; dir: string } | null>(null);
	const [guardado, setGuardado] = useState<number | null>(null);
	const [guardados, setGuardados] = useState<FiltroGuardado[]>([]);
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [rows, setRows] = useState<ProspectRow[]>([]);
	const [total, setTotal] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [counts, setCounts] = useState<{ [k: string]: number }>({});
	const [etapasN, setEtapasN] = useState<{ [k: string]: number }>({});
	// Entradas de hoy (creaciones + recontactos) por equipo, para supervisión y dirección (Esteban, 2026-10-12).
	const [equiposHoy, setEquiposHoy] = useState<EntradaEquipo[]>([]);
	const [periodoEnt, setPeriodoEnt] = useState<string>(() => { try { return periodoDe(localStorage.getItem("crm_periodo_ent") || "hoy").k; } catch (e) { return "hoy"; } });
	const periodoRef = useRef(periodoEnt);
	periodoRef.current = periodoEnt;
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [cols, setCols] = useState<string[]>(leerCols);
	const [panel, setPanel] = useState<boolean>(false);
	const [borrador, setBorrador] = useState<Campos>(VACIO);
	// Anuncios y campañas por los que llegó la gente (filtro de Fuente): se piden al abrir "Más filtros".
	const [anuncios, setAnuncios] = useState<AnunciosFuente | null>(null);
	useEffect(() => {
		if (!panel || anuncios || !owner) return;
		http.prospectsService.anuncios(campusId, { owner }).then((r) => setAnuncios(r.data)).catch(() => setAnuncios({ anuncios: [], campanas: [] }));
	}, [panel]);
	useEffect(() => { setAnuncios(null); }, [campusId, owner]);
	const [verCols, setVerCols] = useState<boolean>(false);
	// Reasignar varios a la vez desde la lista (Deisy, 2026-10-10: "no está la opción de elegir varios registros y pasarlos
	// a otra persona"). Lo mismo que la Cartera: marcados o todos los del filtro, con vista previa y deshacer.
	const [marcados, setMarcados] = useState<number[]>([]);
	const [todoFiltro, setTodoFiltro] = useState<boolean>(false);
	const [reasignar, setReasignar] = useState<boolean>(false);
	const [movido, setMovido] = useState<number | null>(null);
	const [guardar, setGuardar] = useState<{ nombre: string; compartido: boolean } | null>(null);
	const refCols = usePop(verCols, () => setVerCols(false));
	const mover = (k: string, d: number) => setCols((cs) => { const i = cs.indexOf(k), j = i + d; if (i < 0 || j < 0 || j >= cs.length) return cs; const n = [...cs]; [n[i], n[j]] = [n[j], n[i]]; return n; });
	const [arrastrando, setArrastrando] = useState<string | null>(null);
	const soltarEn = (destino: string) => {
		if (!arrastrando || arrastrando === destino) return;
		setCols((cs) => { const n = cs.filter((x) => x !== arrastrando); n.splice(n.indexOf(destino), 0, arrastrando); return n; });
		setArrastrando(null);
	};

	useEffect(() => { if (catalogs && owner === null) setOwner(catalogs.isSupervisor ? "all" : "me"); }, [catalogs]);
	useEffect(() => { http.prospectsService.filtrosGuardados(campusId).then((r) => setGuardados(r.data)).catch(() => { /* sin filtros guardados */ }); }, [campusId]);
	useEffect(() => { try { localStorage.setItem(LLAVE_COLS, JSON.stringify(cols)); } catch { /* sin almacenamiento */ } }, [cols]);

	// Lo que se manda al servidor: vista fija + filtros por campo + orden.
	const filtros = useMemo((): ProspectFilters => ({
		view, etapa: campos.etapas.length ? campos.etapas.join(",") : null, status: campos.status, planteles: campos.planteles, duenas: campos.duenas, equipos: campos.equipos,
		cursos: campos.cursos, source: campos.source, creado_desde: campos.creado_desde || null, creado_hasta: campos.creado_hasta || null,
		contacto_desde: campos.contacto_desde || null, contacto_hasta: campos.contacto_hasta || null,
		sin_contacto_dias: campos.sin_contacto_dias ? Number(campos.sin_contacto_dias) : null, actividad: campos.actividad || null,
		anuncios: campos.anuncios, campanas: campos.campanas, canales: campos.canales,
		sort: orden?.sort || null, dir: orden?.dir || null, gestion,
	}), [view, campos, orden, gestion]);

	const turnoC = useRef<number>(0);
	const turnoL = useRef<number>(0);
	// Los conteos esperan a la lista: en un plantel grande cuentan cientos de miles y no deben retrasar lo que se ve primero.
	const listaLista = useRef<Promise<void>>(Promise.resolve());
	// Entradas por equipo del periodo elegido (solo supervisión); la respuesta vieja no pisa a la nueva.
	const turnoE = useRef<number>(0);
	const cargarEntradas = useCallback(() => {
		if (!sup) return;
		const yo = ++turnoE.current;
		http.prospectsService.entradasEquipos(campusId, periodoRef.current).then((r) => { if (yo === turnoE.current) setEquiposHoy(r.data.equipos || []); }).catch(() => { /* sin conteo por equipo */ });
	}, [campusId, sup]);
	const cargarConteos = useCallback(async () => {
		if (!owner) return;
		const yo = ++turnoC.current;
		await listaLista.current;
		if (yo !== turnoC.current) return;
		try {
			const r = await http.prospectsService.views(campusId, { owner, crm: 1 });
			if (yo !== turnoC.current) return;
			setCounts(r.data.counts || {}); setEtapasN(r.data.etapas || {});
		} catch (e) { if (yo === turnoC.current) setError(errorTexto(e)); }
		cargarEntradas();
		http.prospectsService.misGestiones(campusId).then((r) => { if (yo === turnoC.current) setMisG(r.data.items || []); }).catch(() => { /* sin gestiones */ });
	}, [campusId, owner, sup, cargarEntradas]);
	// Cambiar el periodo pide SOLO las entradas por equipo (no vuelve a contar el riel).
	const primerPeriodo = useRef(true);
	useEffect(() => {
		try { localStorage.setItem("crm_periodo_ent", periodoEnt); } catch (e) { /* sin almacenamiento */ }
		if (primerPeriodo.current) { primerPeriodo.current = false; return; }
		cargarEntradas();
	}, [periodoEnt]); // eslint-disable-line react-hooks/exhaustive-deps
	const cargar = useCallback((p: number): Promise<number[]> => {
		if (!owner) return Promise.resolve([]);
		const yo = ++turnoL.current;
		setLoading(true); setError(null);
		const f: ProspectFilters = q ? { owner, q, lite: 1 } : { owner, lite: 1, ...filtros };
		const pedido = http.prospectsService.list(campusId, f, PER_PAGE, p).then((r) => {
			if (yo !== turnoL.current) return [];
			setRows(r.data.data); setTotal(r.data.total); setPage(p);
			return r.data.data.map((x) => x.id);
		}).catch((e) => { if (yo === turnoL.current) setError(errorTexto(e)); return [] as number[]; })
			.finally(() => { if (yo === turnoL.current) setLoading(false); });
		listaLista.current = pedido.then(() => undefined);
		return pedido;
	}, [campusId, owner, filtros, q]);

	// Primero la lista, luego los conteos (este orden de los efectos importa: cargarConteos espera a listaLista).
	useEffect(() => { cargar(1); }, [cargar]);
	useEffect(() => { setMarcados([]); setTodoFiltro(false); }, [campusId, owner, filtros, q]);
	useEffect(() => { cargarConteos(); }, [cargarConteos, refresco]);
	// Conteos del riel en vivo: un WhatsApp de una persona mía (o cualquiera, si veo carteras ajenas) los vuelve a pedir;
	// lo que cambia solo con el reloj (Vencidas, Hoy), cada minuto con la pestaña a la vista.
	useEffect(() => {
		let t: any;
		let ultima = 0;
		const pedir = () => { clearTimeout(t); t = setTimeout(() => { if (document.visibilityState === "visible") { ultima = Date.now(); cargarConteos(); } }, 1500); };
		const off = push.on("wa", (d) => {
			if (d?.estado) return;
			const { yo } = quienSoy();
			const mio = !!yo && !!d && "o" in d && d.o != null && Number(d.o) === yo;
			// Mi cartera: cada mensaje mío. Carteras ajenas (supervisión): llegan los de toda la escuela, a lo más cada 15 s.
			if (mio || (owner !== "me" && Date.now() - ultima > 15000) || (owner === "me" && (!d || !("o" in d) || d.o == null))) pedir();
		});
		const reloj = setInterval(() => { if (document.visibilityState === "visible") cargarConteos(); }, 60000);
		return () => { off(); clearTimeout(t); clearInterval(reloj); };
	}, [cargarConteos, owner]);
	// Flechas de la ficha (como Zoho): la ficha sabe quién va antes y después en esta lista.
	useEffect(() => { onNav?.({ ids: rows.map((r) => r.id), page, total, perPage: PER_PAGE, ir: cargar }); }, [rows, page, total, cargar]);
	// La ficha guardó algo: misma página, sin brincar arriba.
	useEffect(() => { if (refresco) cargar(page); }, [refresco]);

	const limpiarTodo = () => { setView(null); setGestion(null); setCampos(VACIO); setOrden(null); setGuardado(null); setSearch(""); setQ(""); };
	const elegirGestion = (k: string) => { limpiarTodo(); setGestion(k); };
	const elegirVista = (v: string | null) => { limpiarTodo(); setView(v); };
	const elegirEtapa = (e: number) => { limpiarTodo(); setCampos({ ...VACIO, etapas: [e] }); };
	const aplicarGuardado = (g: FiltroGuardado) => {
		const f = g.filtros || {};
		const arr = (x: any) => (Array.isArray(x) ? x : x === undefined || x === null || x === "" ? [] : String(x).split(","));
		setSearch(""); setQ(""); setView(f.view || null); setGuardado(g.id);
		setCampos({ ...VACIO, status: arr(f.status).map(Number), etapas: arr(f.etapa).map(Number), planteles: arr(f.planteles).map(Number),
			duenas: arr(f.duenas).map(String), equipos: arr(f.equipos).map(String), cursos: arr(f.cursos).map(String), source: arr(f.source).map(Number),
			creado_desde: f.creado_desde || "", creado_hasta: f.creado_hasta || "", contacto_desde: f.contacto_desde || "", contacto_hasta: f.contacto_hasta || "",
			sin_contacto_dias: f.sin_contacto_dias ? String(f.sin_contacto_dias) : "", actividad: f.actividad || "",
			anuncios: arr(f.anuncios).map(String), campanas: arr(f.campanas).map(String), canales: arr(f.canales).map(Number) });
		setOrden(f.sort ? { sort: f.sort, dir: f.dir || "desc" } : null);
	};
	const ordenar = (c: Col) => {
		if (!c.sort) return;
		const k = c.sort;
		setOrden((o) => {
			const actual = o || ORDEN_DEF;
			if (actual.sort === k) return { sort: k, dir: actual.dir === "desc" ? "asc" : "desc" };
			// Actividad: primero la más próxima (Esteban, 2026-10-12).
			return { sort: k, dir: ["nombre", "plantel", "duena", "equipo", "curso", "etapa", "status", "movil", "proxima"].includes(k) ? "asc" : "desc" };
		});
	};
	const guardarFiltro = async () => {
		if (!guardar?.nombre.trim()) return;
		try {
			const r = await http.prospectsService.guardarFiltro(campusId, { id: guardado && guardados.find((g) => g.id === guardado)?.mio ? guardado : undefined,
				nombre: guardar.nombre.trim(), compartido: guardar.compartido, filtros });
			setGuardados(r.data);
			const nuevo = r.data.find((g) => g.nombre === guardar.nombre.trim() && g.mio);
			if (nuevo) setGuardado(nuevo.id);
			setGuardar(null);
		} catch (e) { setError(errorTexto(e)); }
	};
	const borrarFiltro = async (g: FiltroGuardado) => {
		if (!window.confirm("¿Borrar el filtro “" + g.nombre + "”?")) return;
		try { const r = await http.prospectsService.guardarFiltro(campusId, { id: g.id, borrar: true }); setGuardados(r.data); if (guardado === g.id) limpiarTodo(); }
		catch (e) { setError(errorTexto(e)); }
	};

	const n = (v: number | undefined) => (v || 0).toLocaleString("es-MX");
	const todos = owner === "kits" ? "Todos los matriculados" : owner === "territorio" ? "Todos los clientes" : sup ? (owner === "me" ? "Todos mis clientes" : "Todos los clientes") : "Todos mis clientes";
	const gActual = guardados.find((g) => g.id === guardado) || null;
	const gEleg = gestion ? misG.find((x) => x.key === gestion) : null;
	// Viendo "Entradas de <periodo>": solo el rango de entrada del periodo (y si acaso un equipo), sin otros filtros.
	const [entA, entB] = rangoEnt(periodoEnt);
	const pEnt = periodoDe(periodoEnt);
	const soloEntrada = (Object.keys(campos) as (keyof Campos)[]).every((k) => k === "equipos" || k === "creado_desde" || k === "creado_hasta"
		|| (Array.isArray(campos[k]) ? !(campos[k] as any[]).length : !campos[k]));
	const entHoyVista = view === "entradas_hoy";
	// La vista vieja "entradas_hoy" (otros accesos) sigue igual; el periodo del riel filtra por el rango, así riel y lista siempre cuadran.
	const enEntradas = !q && !guardado && !gestion && !view && soloEntrada && campos.creado_desde === entA && campos.creado_hasta === entB;
	const verEntradas = (team: string) => { limpiarTodo(); setCampos({ ...VACIO, equipos: team ? [team] : [], creado_desde: entA, creado_hasta: entB }); };
	const cambiarPeriodo = (k: string) => {
		setPeriodoEnt(k);
		if (enEntradas) { const [a, b] = rangoEnt(k); setCampos({ ...campos, creado_desde: a, creado_hasta: b }); }
	};
	const equipoOn = (team: string) => enEntradas && (team ? campos.equipos.length === 1 && campos.equipos[0] === team : !campos.equipos.length);
	const titulo = q ? "Búsqueda: " + q : gActual ? gActual.nombre : gestion ? "Mis Gestiones · " + (gEleg ? gEleg.objetivo + " " + gEleg.estado : gestion)
		: entHoyVista ? "Entradas de Hoy" + (campos.equipos.length === 1 ? " · " + campos.equipos[0] : "")
		: enEntradas ? "Entradas de " + pEnt.titulo + (campos.equipos.length === 1 ? " · " + campos.equipos[0] : "")
		: view ? FILTROS.find((f) => f.key === view)?.label || ""
		: campos.etapas.length === 1 && cuantos(campos) === 1 ? ETAPAS.find((x) => x.id === campos.etapas[0])?.name || "" : cuantos(campos) ? "Clientes filtrados" : todos;
	const totalCartera = Object.values(etapasN).reduce((t, v) => t + Number(v || 0), 0);
	const desde = total ? (page - 1) * PER_PAGE + 1 : 0;
	const hasta = Math.min(total, page * PER_PAGE);
	// En el orden que cada quien acomodó (arrastrando el encabezado o en "Columnas").
	const visibles = cols.map((k) => COLUMNAS.find((c) => c.k === k)).filter((c): c is Col => !!c && (!c.sup || owner !== "me"));
	const tel = useMedia("(max-width: 700px)");
	const filaCols = tel ? COLS_TEL.map((k) => COLUMNAS.find((c) => c.k === k)).filter((c): c is Col => !!c) : visibles;
	const angosta = useAngosta(), peso = (k: string) => (angosta && PESO_ANGOSTA[k]) || PESO[k] || 90;
	const activos = cuantos(campos);
	const marcar = sup && !tel;
	const enPagina = rows.length > 0 && rows.every((r) => marcados.includes(r.id));
	const nMarcados = todoFiltro ? total : marcados.length;
	const quitarMarcas = () => { setMarcados([]); setTodoFiltro(false); };
	// Lo que vive en "Más filtros" (lo demás está a la vista en la barra).
	const masActivos = [campos.planteles.length, campos.source.length, campos.contacto_desde || campos.contacto_hasta, campos.sin_contacto_dias, campos.actividad,
		campos.anuncios.length, campos.campanas.length, campos.canales.length].filter(Boolean).length;
	const ordenVis = orden || (view || q ? null : ORDEN_DEF);
	const sinNada = !q && !view && !activos && !guardado && !gestion;
	const nombrePlantel = (id: number) => plantelCorto(catalogs, id);
	const nombreSt = (id: number) => catalogs?.statuses.find((s) => s.id === id)?.name || String(id);

	// Chips de lo que está filtrando, cada uno con ×.
	const chips: { t: string; quitar: () => void }[] = [];
	const quitar = (k: keyof Campos, v?: any) => setCampos((c) => ({ ...c, [k]: Array.isArray(c[k]) ? (c[k] as any[]).filter((x) => x !== v) : "" }));
	campos.planteles.forEach((v) => chips.push({ t: "Plantel: " + nombrePlantel(v), quitar: () => quitar("planteles", v) }));
	campos.source.forEach((v) => chips.push({ t: "Llegó por: " + (catalogs?.sources.find((s) => s.id === v)?.name || v), quitar: () => quitar("source", v) }));
	campos.campanas.forEach((v) => chips.push({ t: "Campaña: " + (anuncios?.campanas.find((c) => c.id === v)?.nombre || v), quitar: () => quitar("campanas", v) }));
	campos.anuncios.forEach((v) => chips.push({ t: "Anuncio: " + (anuncios?.anuncios.find((a) => a.id === v)?.nombre || v), quitar: () => quitar("anuncios", v) }));
	campos.canales.forEach((v) => chips.push({ t: "Escribió al: " + (catalogs?.canales?.find((c) => c.id === v)?.label || v), quitar: () => quitar("canales", v) }));
	if (campos.contacto_desde || campos.contacto_hasta) chips.push({ t: "Último contacto: " + (campos.contacto_desde || "…") + " a " + (campos.contacto_hasta || "…"), quitar: () => setCampos((c) => ({ ...c, contacto_desde: "", contacto_hasta: "" })) });
	if (campos.sin_contacto_dias) chips.push({ t: "Sin contacto hace " + campos.sin_contacto_dias + "+ días", quitar: () => quitar("sin_contacto_dias") });
	if (campos.actividad) chips.push({ t: "Próxima actividad: " + (ACTIVIDAD.find((a) => a.k === campos.actividad)?.t || ""), quitar: () => quitar("actividad") });

	const celda = (c: Col, r: ProspectRow) => {
		switch (c.k) {
			case "nombre": {
				// Primer nombre y primer apellido; sin nombre: el de WhatsApp con su logo; sin nada: el teléfono (Esteban, 2026-10-06).
				const corto = nombreLista(r), wa = (r as any).wa_nombre as string | null;
				// Kits ve a todo el territorio (Esteban, 2026-10-14): que se note cuál lleva ella y de quién son las demás.
				// Con la columna Dueñas a la vista lo dice ella ("Tú (Kits)"); sin ella, una etiqueta junto al nombre.
				const sinDuenas = owner === "kits" && !filaCols.some((x) => x.k === "duena");
				const kitDe = sinDuenas ? (r as any).kit_de as string | null : null, kitMia = sinDuenas && !!(r as any).kit_mia;
				return <td key={c.k} className="nom" title={nombreReal(r) || wa || ""}>{corto ? corto : pareceNombre(wa) ? <span className="cl-sin-nombre">{limpiarWa(wa)}<LogoWa /></span>
					: <span className="cl-wa-num">{nombreWa(r.phone_e164)}</span>}
					{kitMia ? <span className="cl-mia" title="Tú llevas su gestión de Kit">Mía</span>
						: kitDe ? <span className="cl-de-otra" title={"Gestión de Kit: " + kitDe}>{kitDe === "Sin asignar" ? "Sin asignar" : primerNombre(kitDe)}</span> : null}</td>;
			}
			case "movil": return (
				<td key={c.k} className="tel c-tel">{r.phone_e164 ? <span className="cl-tel">
					{onChat ? <button type="button" className="cl-chat" title="Abrir su conversación en WappChat" aria-label="Abrir en WappChat"
						onClick={(e) => { e.stopPropagation(); onChat(r.id); }} onKeyDown={(e) => e.stopPropagation()}><LogoWa size={16} label="WappChat" /></button> : null}
					{(() => { const t = telefonoBonito(r.phone_e164), m = t.match(/^(\+\d{1,3}) (.*)$/); return m ? <span><span className="cl-lada">{m[1]} </span>{m[2]}</span> : t; })()}</span> : "—"}</td>
			);
			case "territorio": return <td key={c.k} className="c-terr">{territorio(r) || <span className="crm-apagado">—</span>}</td>;
			case "etapa": {
				const e = (r as any).etapa_id as number, borde = ETAPA_BORDE[e];
				const kitDe = (r as any).kit_de as string | null;
				return <td key={c.k}>{borde ? <span className="cl-etapa" style={{ borderColor: borde, color: borde }}>{ETAPAS.find((x) => x.id === e)?.name}</span> : "—"}
					{kitDe && !filaCols.some((x) => x.k === "duena") ? <small className={"cl-kit-de" + ((r as any).kit_mia ? " mia" : "")} title={"Gestión de Kit: " + kitDe}>Kit · {(r as any).kit_mia ? "Mía" : corto(kitDe)}</small> : null}</td>;
			}
			case "status": {
				const code = catalogs?.statuses.find((x) => x.id === r.status_id)?.code || "";
				const pc = ST_PILL[code] || { bg: "#F4ECE6", fg: "#2B1C16" };
				return <td key={c.k} className="c-st"><span className={"cl-pill" + (pc.lleno ? " lleno" : "")} style={{ background: pc.bg, color: pc.fg }} title={code === "potencial" && r.potencial_motivo ? "Potencial · " + r.potencial_motivo : r.status}>{code === "numero_equivocado" ? "Equivocado" : code === "nuevo_cliente" ? "Nuevo" : r.status}</span></td>;
			}
			case "curso": { const t = r.wapp?.curso ? interesCorto(r.wapp.curso) : cursosCortos(r.course_interest_text); return <td key={c.k} className="c-curso" title={t || undefined}>{t || <span className="crm-apagado">—</span>}</td>; }
			case "plantel": return <td key={c.k} className="c-plantel">{plantelCorto(catalogs, r.campus_id)}</td>;
			case "duena": {
				// Equipo del cliente (Esteban, 2026-10-13): todas sus dueñas, "María González (Ventas)", "Antia González (Kits)".
				const otras = ((r as any).duenas || []) as { o: string; n: string | null }[];
				if (!otras.length) return <td key={c.k} className="c-duena" title={r.owner_zoho_name || ""}>{r.owner_zoho_name ? corto(r.owner_zoho_name) : <span className="crm-apagado" title="Nadie la tiene asignada">Sin asignar</span>}</td>;
				const todas = [{ o: "Venta", n: r.owner_zoho_name as string | null }, ...otras];
				return <td key={c.k} className="c-duena multi" title={todas.map((d) => (d.n || "Sin asignar") + " (" + (AREA[d.o] || d.o) + ")").join("\n")}>
					{todas.map((d, i) => <span key={i} className={"cl-duena" + (d.o === "Kit" && (r as any).kit_mia ? " mia" : "")}>{d.n ? (d.o === "Kit" && (r as any).kit_mia ? "Tú" : inicial(d.n)) : <span className="crm-apagado">Sin asignar</span>} <small>({AREA[d.o] || d.o})</small></span>)}</td>;
			}
			case "equipo": return <td key={c.k}>{(r as any).team_code || <span className="crm-apagado">—</span>}</td>;
			case "fuente": return <td key={c.k}>{(r as any).source || <span className="crm-apagado">—</span>}</td>;
			case "anuncio": return <td key={c.k} className="c-anuncio" title={(r as any).ad_name || ""}>{(r as any).ad_name || <span className="crm-apagado">—</span>}</td>;
			case "creado": {
				// La entrada: creación o, si volvió a escribir, su último recontacto (con ↻ para distinguirlo).
				const ent = (r as any).entrada_at || r.first_contact_at, rec = (r as any).recontacto_at as string | null;
				const esRec = !!rec && !!ent && ent === rec && rec !== r.first_contact_at;
				const corta = (v: string) => { const f = fechaLarga(v).split(",")[0], y = " " + new Date().getFullYear(); return f.endsWith(y) ? f.slice(0, -y.length) : f.replace(/ (\d\d)(\d\d)$/, " $2"); };   // este año sin año: "6 Oct"; otros "22 Jul 24"
				return <td key={c.k} className="c-ult c-fecha" title={ent ? (esRec ? "Recontactó el " + fechaLarga(rec!) + (r.first_contact_at ? " · creado el " + fechaLarga(r.first_contact_at) : "") : "Creado el " + fechaLarga(ent)) : ""}>
					{ent ? <>{esRec ? <span className="cl-rec" aria-label="Recontacto">↻ </span> : null}{corta(ent)}</> : "—"}
					{enEntradas || entHoyVista ? <small className={"cl-entrada" + (esRec ? " rec" : "")}>{esRec ? "Recontacto" : "Creación"}</small> : null}</td>;
			}
			case "contacto": return <td key={c.k} className="c-ult">{cuando(r.last_activity_at)}</td>;
			case "proxima": {
				const b = banderin(r.next_action_at);
				return <td key={c.k} className="c-act" title={(b?.tono === "vencida" ? "Vencida · " : "") + (r.next_action_reason || "")}>{b ? <span className={"cl-flag " + b.tono}>{b.t}</span> : null}</td>;
			}
			default: return <td key={c.k} />;
		}
	};
	const rangoFila = (titulo: string, d: "creado" | "contacto") => (
		<div className="cl-sec">
			<div className="cl-sec-t">{titulo}</div>
			<div className="cl-rangos">
				{RANGOS.map((x) => {
					const [a, b] = x.d();
					const on = borrador[(d + "_desde") as keyof Campos] === a && borrador[(d + "_hasta") as keyof Campos] === b;
					return <button key={x.k} type="button" className={"cl-rango" + (on ? " on" : "")} onClick={() => setBorrador({ ...borrador, [d + "_desde"]: on ? "" : a, [d + "_hasta"]: on ? "" : b })}>{x.t}</button>;
				})}
			</div>
			<div className="cl-fechas">
				<label>Desde<input type="date" value={borrador[(d + "_desde") as keyof Campos] as string} onChange={(e) => setBorrador({ ...borrador, [d + "_desde"]: e.target.value })} /></label>
				<label>Hasta<input type="date" value={borrador[(d + "_hasta") as keyof Campos] as string} onChange={(e) => setBorrador({ ...borrador, [d + "_hasta"]: e.target.value })} /></label>
			</div>
		</div>
	);

	return (
		<div className="crm-lista">
			<div className="crm-riel">
				{/* Sin el selector "Todos los chats" (Esteban, 2026-10-06: no tiene sentido aquí; se filtra con Asesora y Equipo). */}
				{catalogs?.kits && !sup ? (
					<>
						{/* Kits (Esteban, 2026-10-12): su cartera o todos los matriculados de su territorio. */}
						<h4>Ver</h4>
						<button className={"crm-vista" + (owner !== "kits" ? " on" : "")} onClick={() => setOwner("me")}>Mi Cartera</button>
						<button className={"crm-vista" + (owner === "kits" ? " on" : "")} onClick={() => setOwner("kits")}>Matriculados del Territorio</button>
					</>
				) : catalogs?.territorio && !sup ? (
					<>
						{/* Asesora de Admisiones (Esteban, 2026-10-15): su cartera o todos los clientes de su territorio. */}
						<h4>Ver</h4>
						<button className={"crm-vista" + (owner !== "territorio" ? " on" : "")} onClick={() => setOwner("me")}>Mi Cartera</button>
						<button className={"crm-vista" + (owner === "territorio" ? " on" : "")} onClick={() => setOwner("territorio")}>Clientes del Territorio</button>
					</>
				) : null}
				<h4>Filtros</h4>
				<button className={"crm-vista" + (sinNada ? " on" : "")} onClick={() => elegirVista(null)}>{owner === "kits" ? "Todos los matriculados" : todos} <span className="n">{n(totalCartera)}</span></button>
				{FILTROS.map((f) => (
					<button key={f.key} className={"crm-vista" + (!q && view === f.key && !guardado ? " on" : "")} onClick={() => elegirVista(f.key)}>
						{f.label} <span className={"n" + (f.oro && counts[f.key] ? " oro" : "")}>{n(counts[f.key])}</span>
					</button>
				))}
				{misG.length ? (
					<>
						<h4>Mis Gestiones</h4>
						{misG.map((g) => (
							<button key={g.key} className={"crm-vista" + (!q && gestion === g.key ? " on" : "")} onClick={() => elegirGestion(g.key)}>
								{g.objetivo} · {g.estado} <span className={"n" + (g.malo ? " oro" : "")}>{n(g.n)}</span>
							</button>
						))}
					</>
				) : null}
				{sup ? (
					<>
						<h4 className="cl-ent-cab">Entradas <SelPeriodo valor={periodoEnt} onChange={cambiarPeriodo} /></h4>
						{equiposHoy.map((e) => (
							<button key={e.team || "-"} className={"crm-vista cl-eq-hoy" + (equipoOn(e.team) ? " on" : "")} onClick={() => verEntradas(e.team)}>
								<span>{e.team || "Sin equipo"}<small><CyR c={e.creaciones} r={e.recontactos} /></small></span> <span className="n">{n(e.total)}</span>
							</button>
						))}
						{!equiposHoy.length ? <div className="cl-ent-vacio">Sin entradas</div> : null}
					</>
				) : null}
				<h4>Mis filtros</h4>
				{guardados.filter((g) => g.mio).map((g) => (
					<div key={g.id} className={"crm-vista cl-guardado" + (guardado === g.id ? " on" : "")}>
						<button className="cl-g-nom" onClick={() => aplicarGuardado(g)}>{g.nombre}{g.compartido ? <span className="cl-comp" title="Lo ve todo el plantel">compartido</span> : null}</button>
						<button className="cl-g-x" onClick={() => borrarFiltro(g)} aria-label={"Borrar " + g.nombre}>×</button>
					</div>
				))}
				{!guardados.some((g) => g.mio) ? <div className="cl-vacio">Arma un filtro y toca “Guardar filtro”.</div> : null}
				{guardados.some((g) => !g.mio) ? (
					<>
						<h4>Del plantel</h4>
						{guardados.filter((g) => !g.mio).map((g) => (
							<button key={g.id} className={"crm-vista" + (guardado === g.id ? " on" : "")} onClick={() => aplicarGuardado(g)} title={g.autor ? "De " + g.autor : ""}>{g.nombre}</button>
						))}
					</>
				) : null}
				<h4>Etapa</h4>
				{ETAPAS.map((e) => (
					<button key={e.id} className={"crm-vista" + (!q && !guardado && !view && campos.etapas.length === 1 && campos.etapas[0] === e.id && activos === 1 ? " on" : "")} onClick={() => elegirEtapa(e.id)}>
						{e.name} <span className="n">{n(etapasN[e.id])}</span>
					</button>
				))}
			</div>

			<div className="crm-centro">
				{/* Pantalla angosta: el riel se esconde y los filtros pasan a este selector. */}
				<select className="crm-movil" aria-label="Filtro" value={gestion ? "m:" + gestion : view ? "v:" + view : guardado ? "g:" + guardado : ""}
					onChange={(e) => { const v = e.target.value; if (v.startsWith("m:")) elegirGestion(v.slice(2)); else if (v.startsWith("v:")) elegirVista(v.slice(2)); else if (v.startsWith("g:")) { const g = guardados.find((x) => x.id === Number(v.slice(2))); if (g) aplicarGuardado(g); } else elegirVista(null); }}>
					<option value="">{todos}</option>
					{FILTROS.map((f) => <option key={f.key} value={"v:" + f.key}>{f.label + " · " + n(counts[f.key])}</option>)}
					{misG.map((g) => <option key={"m" + g.key} value={"m:" + g.key}>{"Mis Gestiones: " + g.objetivo + " " + g.estado + " · " + n(g.n)}</option>)}
					{guardados.map((g) => <option key={g.id} value={"g:" + g.id}>{g.nombre}</option>)}
				</select>
				{/* Teléfono, supervisión: las entradas por equipo en una tira que se desliza (en PC están en el riel). */}
				{sup && !q ? (
					<div className="cl-ent-movil">
						<div className="cl-ent-movil-cab"><b>Entradas</b><SelPeriodo valor={periodoEnt} onChange={cambiarPeriodo} /></div>
						<div className="cl-ent-tira">
							{equiposHoy.map((e) => (
								<button key={e.team || "-"} className={"cl-ent-chip" + (equipoOn(e.team) ? " on" : "")} onClick={() => (equipoOn(e.team) ? limpiarTodo() : verEntradas(e.team))}>
									{e.team || "Sin equipo"} <b>{n(e.total)}</b>
								</button>
							))}
							{!equiposHoy.length ? <span className="cl-ent-vacio">Sin entradas</span> : null}
						</div>
					</div>
				) : null}
				<div className="crm-cab">
					<h2>{titulo}</h2>
					<div className="busca">
						<input type="search" placeholder="Buscar por nombre o teléfono" value={search} aria-label="Buscar por nombre o teléfono"
							onChange={(e) => setSearch(e.target.value)}
							onKeyDown={(e) => { if (e.key === "Enter") setQ(search.trim()); if (e.key === "Escape") { setSearch(""); setQ(""); } }} />
						{q ? <button className="limpiar" onClick={() => { setSearch(""); setQ(""); }} aria-label="Quitar búsqueda">×</button> : null}
					</div>
					<button className="crm-b negro" onClick={onNuevo}>+ Nuevo Prospecto</button>
				</div>

				<div className="cl-barra">
					<FiltroFecha titulo="Creado / recontacto" desde={campos.creado_desde} hasta={campos.creado_hasta} onChange={(d, h) => { setCampos({ ...campos, creado_desde: d, creado_hasta: h }); setGuardado(null); }} />
					<MultiFiltro titulo="Etapa" ops={ETAPAS.map((e) => ({ v: e.id, t: e.name }))} sel={campos.etapas} onChange={(v) => { setCampos({ ...campos, etapas: v }); setGuardado(null); }} />
					<MultiFiltro titulo="Status" ops={(catalogs?.statuses || []).map((x) => ({ v: x.id, t: x.name }))} sel={campos.status} onChange={(v) => { setCampos({ ...campos, status: v }); setGuardado(null); }} />
					{sup && owner !== "me" ? <MultiFiltro titulo="Asesora" ops={[{ v: "none", t: "Sin asesora" }, ...(catalogs?.advisors || []).filter((a) => !campos.equipos.length || campos.equipos.includes(a.team || "")).map((a) => ({ v: a.id, t: a.name + (a.team && !campos.equipos.length ? " · " + a.team : "") }))]} sel={campos.duenas} onChange={(v) => { setCampos({ ...campos, duenas: v }); setGuardado(null); }} buscar /> : null}
					{sup && owner !== "me" && (catalogs?.teams || []).length ? <MultiFiltro titulo="Equipo" ops={(catalogs?.teams || []).map((t) => ({ v: t, t }))} sel={campos.equipos} onChange={(v) => { setCampos({ ...campos, equipos: v }); setGuardado(null); }} /> : null}
					{(catalogs?.misPlanteles || []).length > 1 ? <MultiFiltro titulo="Todos mis planteles" ops={(catalogs?.misPlanteles || []).map((c) => ({ v: c.id, t: c.name }))} sel={campos.planteles} onChange={(v) => { setCampos({ ...campos, planteles: v }); setGuardado(null); }} /> : null}
					<MultiFiltro titulo="Curso" ops={CURSOS.map((c) => ({ v: c, t: interesCorto(c) }))} sel={campos.cursos} onChange={(v) => { setCampos({ ...campos, cursos: v }); setGuardado(null); }} buscar />
					<button className={"crm-b chico cl-filtrar" + (masActivos ? " activo" : "")} onClick={() => { setBorrador(campos); setPanel(true); }} aria-expanded={panel}>
						<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 5h18l-7 8v6l-4 2v-8z" /></svg>
						Más filtros{masActivos ? " · " + masActivos : ""}
					</button>
					{activos || orden || view ? <button className="cl-link" onClick={limpiarTodo}>Limpiar todo</button> : null}
					<span className="cl-espacio" />
					{(activos || view || orden) && !q ? <button className="crm-b chico" onClick={() => setGuardar({ nombre: gActual?.mio ? gActual.nombre : "", compartido: !!gActual?.compartido })}>{gActual?.mio ? "Actualizar filtro" : "Guardar filtro"}</button> : null}
					<div className="cl-cols" ref={refCols}>
						<button className="crm-b chico" onClick={() => setVerCols(!verCols)} aria-expanded={verCols}>Columnas</button>
						{verCols ? (
							<div className="cl-pop" role="dialog" aria-label="Columnas">
								<div className="cl-pop-t">Columnas a la vista</div>
								<div className="cl-pop-nota">Arrastra los encabezados de la tabla o usa las flechas para cambiar el orden.</div>
								{[...cols.map((k) => COLUMNAS.find((c) => c.k === k)!).filter(Boolean), ...COLUMNAS.filter((c) => !cols.includes(c.k))].filter((c) => !c.sup || owner !== "me").map((c) => (
									<div key={c.k} className="cl-col-fila">
									{cols.includes(c.k) ? (
										<span className="cl-col-flechas">
											<button type="button" aria-label={"Subir " + c.t} disabled={cols.indexOf(c.k) <= 0} onClick={() => mover(c.k, -1)}>↑</button>
											<button type="button" aria-label={"Bajar " + c.t} disabled={cols.indexOf(c.k) >= cols.length - 1} onClick={() => mover(c.k, 1)}>↓</button>
										</span>
									) : <span className="cl-col-flechas" />}
									<label className="cl-casilla">
										<input type="checkbox" checked={cols.includes(c.k)} disabled={c.k === "nombre"} onChange={() => setCols(cols.includes(c.k) ? cols.filter((x) => x !== c.k) : [...cols, c.k])} />{c.t}
									</label>
									</div>
								))}
								<button className="cl-link" onClick={() => setCols(COLUMNAS.filter((c) => c.def).map((c) => c.k))}>Volver a las de siempre</button>
							</div>
						) : null}
					</div>
				</div>
				{enEntradas && sup ? (() => {
					const eq = equiposHoy.find((e) => campos.equipos.length === 1 ? e.team === campos.equipos[0] : false);
					return eq ? (
						<>
							{/* Teléfono: el total del equipo en una tarjetita (en PC ya está en el riel). */}
							<div className="cl-eq-resumen"><span><b>{eq.team} · {pEnt.t}</b><small><CyR c={eq.creaciones} r={eq.recontactos} /></small></span><span className="n">{n(eq.total)}</span></div>
							<div className="cl-eq-desglose" aria-label={"Entradas de " + eq.team + " por asesora"}>
								{eq.asesoras.map((a) => <span key={a.nombre || "-"} className="cl-eq-as">
									<b>{a.nombre ? corto(a.nombre) : "Sin asignar"}</b> {a.total}<small><CyR c={a.creaciones} r={a.recontactos} /></small></span>)}
							</div>
						</>
					) : null;
				})() : null}
				{chips.length ? (
					<div className="cl-chips">{chips.map((c, i) => <span key={i} className="cl-chip">{c.t}<button onClick={() => { c.quitar(); setGuardado(null); }} aria-label={"Quitar " + c.t}>×</button></span>)}</div>
				) : null}
				{guardar ? (
					<div className="cl-guardar">
						<input autoFocus placeholder="Nombre del filtro (ej. Barbería de octubre sin contacto)" value={guardar.nombre} maxLength={80}
							onChange={(e) => setGuardar({ ...guardar, nombre: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") guardarFiltro(); if (e.key === "Escape") setGuardar(null); }} />
						{sup ? <label className="cl-casilla"><input type="checkbox" checked={guardar.compartido} onChange={(e) => setGuardar({ ...guardar, compartido: e.target.checked })} />Compartir con el plantel</label> : null}
						<button className="crm-b chico negro" disabled={!guardar.nombre.trim()} onClick={guardarFiltro}>Guardar</button>
						<button className="crm-b chico" onClick={() => setGuardar(null)}>Cancelar</button>
					</div>
				) : null}
				{error ? <div className="crm-aviso error">{error}</div> : null}
				{movido !== null ? <div className="crm-aviso ok">Listo: {n(movido)} {movido === 1 ? "persona reasignada" : "personas reasignadas"}. Se puede deshacer en Cartera › Salidas y coberturas.<button className="cl-link" onClick={() => setMovido(null)}>Cerrar</button></div> : null}
				{marcar && nMarcados > 0 ? (
					<div className="cl-marcados" role="region" aria-label="Marcados">
						<b>{n(nMarcados)} {nMarcados === 1 ? "marcada" : "marcadas"}</b>
						<button className="crm-b chico negro" onClick={() => setReasignar(true)}>Reasignar</button>
						<button className="cl-link" onClick={quitarMarcas}>Quitar marcas</button>
						{!todoFiltro && enPagina && total > rows.length ? <button className="cl-link" onClick={() => setTodoFiltro(true)}>Marcar los {n(total)} de esta lista</button> : null}
					</div>
				) : null}
				{/* Cargando: barrita arriba, las filas se siguen leyendo (antes se ponían a media opacidad y parecía en blanco). */}
				<div className={"crm-tabla-caja" + (loading ? " cl-cargando" : "")} aria-busy={loading}>
					<table className="crm-tabla cl-fija">
						<colgroup>{marcar ? <col style={{ width: 36 }} /> : null}{(() => { const tot = visibles.reduce((t, c) => t + peso(c.k), 0); return visibles.map((c) => <col key={c.k} style={{ width: (peso(c.k) / tot * 100).toFixed(2) + "%" }} />); })()}</colgroup>
						<thead>
							<tr>
								{marcar ? <th className="cl-marca"><input type="checkbox" aria-label="Marcar los de esta página" checked={todoFiltro || enPagina}
									onChange={(e) => { setTodoFiltro(false); setMarcados(e.target.checked ? Array.from(new Set([...marcados, ...rows.map((r) => r.id)])) : marcados.filter((id) => !rows.some((r) => r.id === id))); }} /></th> : null}
								{visibles.map((c) => {
									const on = !!c.sort && ordenVis?.sort === c.sort;
									const dirs = c.sort ? DIR_TXT[c.sort] : null;
									return (
										<th key={c.k} aria-sort={on ? (ordenVis!.dir === "asc" ? "ascending" : "descending") : undefined}
											className={(c.sort ? "cl-ord" + (on ? " on" : "") : "") + (arrastrando === c.k ? " cl-arrastra" : "")}
											draggable onDragStart={(e) => { setArrastrando(c.k); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => setArrastrando(null)}
											onDragOver={(e) => { if (arrastrando) e.preventDefault(); }} onDrop={(e) => { e.preventDefault(); soltarEn(c.k); }} title="Arrastra para mover la columna">
											{c.sort ? <button onClick={() => ordenar(c)} title={dirs ? "Ordenar: " + (on && ordenVis!.dir === "desc" ? dirs[0] : dirs[1]) : "Ordenar"}>{c.t}<span className={"cl-flecha" + (on ? "" : " off")} aria-hidden="true">{on ? (ordenVis!.dir === "asc" ? "▲" : "▼") : "⇅"}</span></button> : c.t}
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{rows.map((r) => (
								<tr key={r.id} className={"fila" + (todoFiltro || marcados.includes(r.id) ? " marcada" : "") + (owner === "kits" && (r as any).kit_mia ? " kit-mia" : "")} tabIndex={0} onClick={() => onOpen(r.id, titulo)}
									onKeyDown={(e) => { if (e.key === "Enter") onOpen(r.id, titulo); }}>
									{marcar ? <td className="cl-marca" onClick={(e) => e.stopPropagation()}><input type="checkbox" aria-label={"Marcar a " + (nombreLista(r) || r.phone_e164 || "")} checked={todoFiltro || marcados.includes(r.id)}
										onChange={(e) => { setTodoFiltro(false); setMarcados(e.target.checked ? [...marcados, r.id] : marcados.filter((x) => x !== r.id)); }} /></td> : null}
									{filaCols.map((c) => { const td = celda(c, r); return React.cloneElement(td, { className: ((td.props.className || "") + " k-" + c.k).trim() }); })}
								</tr>
							))}
						</tbody>
					</table>
					{!rows.length ? <div className="ventas-vacio">{loading || !owner ? "Cargando…" : "Nadie en esta lista."}</div> : null}
				</div>
				<div className="crm-pag">
					<span>{loading && rows.length ? <span className="cl-cargando-txt">Cargando… </span> : null}{n(desde)}–{n(hasta)} de {n(total)}{ordenVis ? <span className="cl-orden-txt"> · Ordenado por <b>{COLUMNAS.find((c) => c.sort === ordenVis.sort)?.t}</b>, {DIR_TXT[ordenVis.sort]?.[ordenVis.dir === "asc" ? 0 : 1]}</span> : null}</span>
					<span>
						<button className="crm-b chico" disabled={page <= 1 || loading} onClick={() => cargar(page - 1)}>Anterior</button>
						<button className="crm-b chico" disabled={hasta >= total || loading} onClick={() => cargar(page + 1)}>Siguiente</button>
					</span>
				</div>
			</div>

			{reasignar ? <MoverModal campusId={campusId} catalogs={catalogs} ids={marcados} todos={todoFiltro} filtros={q ? { owner, q } : { owner, ...filtros }} cuantos={nMarcados}
				onClose={() => setReasignar(false)} onHecho={(r) => { setReasignar(false); quitarMarcas(); setMovido(r.reasignados); cargar(page); cargarConteos(); }} /> : null}
			{panel ? (
				<div className="cl-velo" onClick={() => setPanel(false)}>
					<aside className="cl-panel" role="dialog" aria-label="Más filtros" onClick={(e) => e.stopPropagation()}>
						<div className="cl-panel-cab"><h3>Más filtros</h3><button className="cl-x" onClick={() => setPanel(false)} aria-label="Cerrar">×</button></div>
						<div className="cl-panel-cuerpo">
							{rangoFila("Último contacto", "contacto")}
							<div className="cl-sec"><div className="cl-sec-t">Sin contacto hace más de</div>
								<div className="cl-rangos">{["7", "15", "30", "60", "90"].map((d) => (
									<button key={d} type="button" className={"cl-rango" + (borrador.sin_contacto_dias === d ? " on" : "")} onClick={() => setBorrador({ ...borrador, sin_contacto_dias: borrador.sin_contacto_dias === d ? "" : d })}>{d} días</button>
								))}</div></div>
							<div className="cl-sec"><div className="cl-sec-t">Próxima actividad</div>
								<div className="cl-rangos">{ACTIVIDAD.map((a) => (
									<button key={a.k} type="button" className={"cl-rango" + (borrador.actividad === a.k ? " on" : "")} onClick={() => setBorrador({ ...borrador, actividad: a.k })}>{a.t}</button>
								))}</div></div>
							<div className="cl-sec"><div className="cl-sec-t">Plantel</div>
								<Casillas ops={(catalogs?.campuses || []).map((c) => ({ v: c.id, t: c.name.replace("Washington Academy ", "") }))} sel={borrador.planteles} onChange={(v) => setBorrador({ ...borrador, planteles: v })} /></div>
							<div className="cl-sec"><div className="cl-sec-t">Llegó por</div>
								<Casillas ops={(catalogs?.sources || []).map((s) => ({ v: s.id, t: s.name }))} sel={borrador.source} onChange={(v) => setBorrador({ ...borrador, source: v })} buscar /></div>
							{anuncios?.campanas.length ? <div className="cl-sec"><div className="cl-sec-t">Campaña <span className="cl-sec-n">últimos 90 días</span></div>
								<Casillas una ops={anuncios.campanas.map((c) => ({ v: c.id, t: c.nombre, n: c.n }))} sel={borrador.campanas} onChange={(v) => setBorrador({ ...borrador, campanas: v })} buscar={anuncios.campanas.length > 8} /></div> : null}
							{anuncios?.anuncios.length ? <div className="cl-sec"><div className="cl-sec-t">Anuncio <span className="cl-sec-n">últimos 90 días</span></div>
								<Casillas una ops={anuncios.anuncios.map((a) => ({ v: a.id, t: a.nombre, n: a.n }))} sel={borrador.anuncios} onChange={(v) => setBorrador({ ...borrador, anuncios: v })} buscar={anuncios.anuncios.length > 8} /></div> : null}
							{catalogs?.canales?.length ? <div className="cl-sec"><div className="cl-sec-t">Escribió al número</div>
								<Casillas ops={catalogs.canales.map((c) => ({ v: c.id, t: c.label || c.display_phone }))} sel={borrador.canales} onChange={(v) => setBorrador({ ...borrador, canales: v })} buscar /></div> : null}
						</div>
						<div className="cl-panel-pie">
							<button className="crm-b" onClick={() => setBorrador({ ...borrador, planteles: [], source: [], contacto_desde: "", contacto_hasta: "", sin_contacto_dias: "", actividad: "", anuncios: [], campanas: [], canales: [] })}>Limpiar</button>
							<button className="crm-b naranja" onClick={() => { setCampos(borrador); setGuardado(null); setSearch(""); setQ(""); setPanel(false); }}>Aplicar{cuantos(borrador) ? " (" + cuantos(borrador) + ")" : ""}</button>
						</div>
					</aside>
				</div>
			) : null}
		</div>
	);
};

export default CrmLista;
